import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Phone,
  MapPin,
  ArrowRight,
  Stethoscope,
  Activity,
  ScanLine,
  ShieldCheck,
  Salad,
  Waves,
  GraduationCap,
  ClipboardCheck,
  MessagesSquare,
  Award,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Seo, physicianSchema } from "@/components/Seo";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { SITE } from "@/site";

const CARE_CARDS = [
  {
    icon: Activity,
    title: "Digestive Health",
    text: "Diagnosis and treatment of acidity, reflux, ulcers, gas, bloating and stomach pain. Every step of the treatment is explained clearly.",
    hindi: "एसिडिटी, अल्सर, गैस और पेट दर्द का इलाज।",
  },
  {
    icon: Waves,
    title: "Liver & Hepatitis Care",
    text: "Treatment of fatty liver, hepatitis B and C, jaundice and cirrhosis, with focus on early detection and regular follow-up.",
    hindi: "फैटी लीवर, हेपेटाइटिस, पीलिया और सिरोसिस का इलाज।",
  },
  {
    icon: ScanLine,
    title: "Endoscopy & Diagnostics",
    text: "Upper GI endoscopy and colonoscopy done with a gentle, patient-first approach so the diagnosis is accurate.",
    hindi: "एंडोस्कोपी और कोलोनोस्कोपी से सही जाँच।",
  },
  {
    icon: ShieldCheck,
    title: "Gallbladder & Pancreas",
    text: "Evaluation of gallstones, bile duct blockage and pancreatic problems. Surgery is advised only when genuinely needed.",
    hindi: "पित्त की पथरी और अग्न्याशय की समस्याओं का इलाज।",
  },
  {
    icon: Salad,
    title: "IBS & Motility",
    text: "Practical long-term plans for IBS, chronic constipation and chronic diarrhoea, based on diet, routine and medicines.",
    hindi: "IBS, कब्ज़ और दस्तों का दीर्घकालिक प्रबंधन।",
  },
  {
    icon: Stethoscope,
    title: "Preventive Gastro Care",
    text: "Timely screening and check-ups to catch liver and digestive problems early, before they become serious.",
    hindi: "समय पर जाँच — बड़ी बीमारी से बचाव।",
  },
];

const CONDITIONS = [
  { label: "Acidity & GERD", hash: "digestive-disorders" },
  { label: "Peptic Ulcer", hash: "digestive-disorders" },
  { label: "IBS", hash: "digestive-disorders" },
  { label: "Ulcerative Colitis", hash: "digestive-disorders" },
  { label: "Crohn's Disease", hash: "digestive-disorders" },
  { label: "Chronic Constipation", hash: "digestive-disorders" },
  { label: "Chronic Diarrhoea", hash: "digestive-disorders" },
  { label: "Bloating & Gas", hash: "digestive-disorders" },
  { label: "Celiac Disease", hash: "digestive-disorders" },
  { label: "Difficulty Swallowing", hash: "digestive-disorders" },
  { label: "Fatty Liver", hash: "liver-diseases" },
  { label: "Hepatitis B", hash: "liver-diseases" },
  { label: "Hepatitis C", hash: "liver-diseases" },
  { label: "Jaundice", hash: "liver-diseases" },
  { label: "Cirrhosis", hash: "liver-diseases" },
  { label: "Alcoholic Liver Disease", hash: "liver-diseases" },
  { label: "Gallstones", hash: "pancreatic-gallbladder" },
  { label: "Pancreatitis", hash: "pancreatic-gallbladder" },
  { label: "Bile Duct Blockage", hash: "pancreatic-gallbladder" },
  { label: "Endoscopy", hash: "diagnostic-procedures" },
  { label: "Colonoscopy", hash: "diagnostic-procedures" },
  { label: "Polyp Removal", hash: "diagnostic-procedures" },
  { label: "GI Bleeding", hash: "diagnostic-procedures" },
  { label: "Liver Fibrosis Test", hash: "diagnostic-procedures" },
];

const WHY = [
  {
    icon: GraduationCap,
    title: "DM (Gastroenterology), IMS BHU",
    text: "Super-specialist degree in gastroenterology from the Institute of Medical Sciences, BHU — one of India's most respected medical institutes.",
  },
  {
    icon: Award,
    title: "Reputed & experienced",
    text: "A well-known name in Varanasi for stomach and liver problems. Patients come from Lanka, Sunderpur, BHU and districts across Purvanchal.",
  },
  {
    icon: ClipboardCheck,
    title: "Evidence-based treatment",
    text: "Treatment follows established medical guidelines. Tests and medicines are advised only when they are actually needed.",
  },
  {
    icon: MessagesSquare,
    title: "Clear communication",
    text: "Your problem and its treatment are explained in simple Hindi or English, so you can decide with full understanding.",
  },
];

const FAQS = [
  {
    q: "Do I need an appointment to consult Dr. Dixit?",
    a: "Yes, consultations are by prior appointment so that every patient gets enough time. Call +91 93696 98080 to book a slot.",
  },
  {
    q: "What are the clinic timings?",
    a: "The clinic is open Monday to Sunday, 9:00 AM to 5:00 PM. Please call and book your appointment before visiting.",
  },
  {
    q: "Where is the clinic located?",
    a: "The clinic is at B 31/18-A-D-3, Rashmi Nagar Colony, behind V2 Mall, Saket Nagar Colony, Lanka, Varanasi – 221005. It is easy to reach from Lanka, Sunderpur and the BHU area.",
  },
  {
    q: "What should I bring to my first visit?",
    a: "Bring your previous prescriptions, blood reports, ultrasound or endoscopy reports, and a list of medicines you are taking. This helps avoid repeating tests.",
  },
  {
    q: "Is endoscopy painful?",
    a: "Most patients tolerate endoscopy well. The throat is numbed with a spray, the procedure takes only a few minutes, and you can usually go home shortly after.",
  },
  {
    q: "How should I prepare for an endoscopy?",
    a: "Do not eat or drink anything for at least 6 to 8 hours before an upper GI endoscopy. If you take regular medicines, tell us while booking so we can guide you. For colonoscopy, the preparation is explained in detail in advance.",
  },
  {
    q: "Do you treat fatty liver disease?",
    a: "Yes. Fatty liver is one of the most common problems we treat. The condition is graded accurately, and treatment includes diet, activity and medicines where needed.",
  },
  {
    q: "Which areas do patients come from?",
    a: "Patients visit from Lanka, Sunderpur, BHU and across Varanasi, as well as from districts across Purvanchal and Eastern Uttar Pradesh.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const Hindi = ({ children, className = "" }) => (
  <p className={`font-hindi ${className}`}>{children}</p>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yCurves = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="hero-grid-lines relative overflow-hidden bg-gradient-to-b from-white to-sky-50/60"
    >
      <motion.svg
        style={{ y: yCurves }}
        className="pointer-events-none absolute -bottom-20 -right-24 opacity-35"
        width="520"
        height="520"
        viewBox="0 0 200 200"
        fill="none"
        aria-hidden="true"
      >
        <circle cx="100" cy="100" r="88" stroke="#BAE6FD" strokeWidth="0.8" />
        <circle cx="100" cy="100" r="64" stroke="#BAE6FD" strokeWidth="0.8" />
        <path
          d="M52 44c30 0 52 17 52 39 0 17-13 26-26 26-11 0-19-7-19-15 0-8 7-13 15-13"
          stroke="#0284C7"
          strokeOpacity="0.4"
          strokeWidth="1.4"
          strokeLinecap="round"
        />
        <circle cx="52" cy="44" r="3.5" fill="#0D9488" />
      </motion.svg>

      <div className="mx-auto max-w-6xl px-5 sm:px-8 pt-12 pb-16 sm:pt-16 sm:pb-20 grid gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-testid="hero-eyebrow-badge"
            className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-white px-4 py-1.5 text-xs sm:text-sm font-semibold tracking-wide text-sky-700"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-teal-500" />
            Gastroenterologist in Varanasi
          </motion.div>

          <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] text-navy">
            <MaskedLine delay={0.15}>Dr. Dixit,</MaskedLine>
            <MaskedLine delay={0.3}>
              DM (Gastro) <span className="text-sky-600">– IMS BHU</span>
            </MaskedLine>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
          >
            <p className="mt-6 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed">
              Dr. Dixit is a reputed and well-known gastroenterologist and
              liver specialist in Varanasi. He treats stomach, intestine,
              liver, gallbladder and pancreas problems — including acidity,
              fatty liver, jaundice, IBS, endoscopy and colonoscopy. The
              clinic is in Lanka, behind V2 Mall.
            </p>
            <Hindi className="mt-3 max-w-xl text-base text-slate-500 leading-relaxed">
              पेट, आंत, लीवर, पित्ताशय और अग्न्याशय की बीमारियों का विशेषज्ञ
              इलाज — लंका, वाराणसी।
            </Hindi>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/contact"
              data-testid="hero-book-appointment-button"
              className="inline-flex items-center gap-2 rounded-full bg-navy px-7 py-3.5 font-heading text-base font-bold text-white transition-all duration-200 hover:bg-navy-800 hover:-translate-y-0.5"
            >
              Book Appointment
              <ArrowRight size={17} strokeWidth={2.5} />
            </Link>
            <a
              href={SITE.phoneHref}
              data-testid="hero-call-now-button"
              className="inline-flex items-center gap-2 rounded-full border-2 border-navy/15 bg-white px-7 py-3.5 font-heading text-base font-bold text-navy transition-all duration-200 hover:border-sky-400 hover:text-sky-700 hover:-translate-y-0.5"
            >
              <Phone size={17} strokeWidth={2.5} />
              Call {SITE.phoneDisplay}
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-8 text-sm font-medium text-slate-500"
            data-testid="hero-trust-line"
          >
            Trusted by patients across Purvanchal.
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="relative hidden sm:block"
        >
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="relative"
          >
            <img
              src="/images/gastro-anatomy-real.png"
              alt="Illustration of the human digestive system — liver, stomach, gallbladder and intestines"
              data-testid="hero-anatomy-image"
              className="mx-auto h-auto w-full max-w-[320px] drop-shadow-[0_24px_40px_rgba(11,30,54,0.16)]"
              loading="eager"
            />
            <div className="mt-5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
              <span className="text-sm font-semibold text-navy">
                Stomach · Liver · Intestine · Pancreas
              </span>
              <span className="font-hindi text-sm text-sky-700">
                पेट और लीवर की विशेषज्ञ देखभाल
              </span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

const AppointmentStrip = () => (
  <section className="bg-navy" data-testid="appointment-strip">
    <div className="mx-auto max-w-6xl px-5 sm:px-8 py-12 sm:py-14 flex flex-col md:flex-row md:items-center gap-8 md:gap-12">
      <div className="flex-1">
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-sky-300">
          Call for Appointment · <span className="font-hindi normal-case tracking-normal">अपॉइंटमेंट के लिए कॉल करें</span>
        </p>
        <a
          href={SITE.phoneHref}
          data-testid="appointment-strip-phone-link"
          className="mt-3 block font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-white transition-colors hover:text-sky-300"
        >
          {SITE.phoneDisplay}
        </a>
        <p className="mt-3 text-sm text-slate-400">
          {SITE.hours} · Lanka, Varanasi
        </p>
      </div>
      <div className="flex flex-wrap gap-4">
        <a
          href={SITE.phoneHref}
          data-testid="appointment-strip-call-button"
          className="inline-flex items-center gap-2 rounded-full bg-sky-500 px-7 py-3.5 font-heading text-base font-bold text-white transition-all duration-200 hover:bg-sky-400 hover:-translate-y-0.5"
        >
          <Phone size={17} strokeWidth={2.5} />
          Call Now
        </a>
        <a
          href={SITE.mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="appointment-strip-location-button"
          className="inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-3.5 font-heading text-base font-bold text-white transition-all duration-200 hover:border-sky-300 hover:text-sky-300 hover:-translate-y-0.5"
        >
          <MapPin size={17} strokeWidth={2.5} />
          View Location
        </a>
      </div>
    </div>
  </section>
);

const About = () => (
  <section className="mx-auto max-w-6xl px-5 sm:px-8 py-20 sm:py-24" data-testid="about-section">
    <div className="grid gap-10 lg:grid-cols-5 lg:gap-14 items-start">
      <Reveal className="lg:col-span-3">
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-sky-600">
          About Dr. Dixit
        </p>
        <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy">
          A reputed gastro &amp; liver specialist, practising in Varanasi
        </h2>
        <p className="mt-5 text-base text-slate-600 leading-relaxed">
          Dr. Dixit is a reputed and well-known gastroenterologist and liver
          specialist in Varanasi. He completed his DM in Gastroenterology from
          the Institute of Medical Sciences, Banaras Hindu University (IMS
          BHU), one of India's most respected medical institutes.
        </p>
        <p className="mt-4 text-base text-slate-600 leading-relaxed">
          He is a senior gastro and liver specialist practising in Lanka,
          Varanasi. Patients consult him not only from Lanka, Sunderpur and
          BHU, but also from districts across Purvanchal and Eastern Uttar
          Pradesh.
        </p>
        <p className="mt-4 text-base text-slate-600 leading-relaxed">
          His way of working is simple: listen to the patient carefully, do
          only the tests that are needed, explain the problem in plain
          language, and treat with evidence-based medicine. From common
          problems like acidity and fatty liver to procedures like endoscopy
          and colonoscopy, patients are guided at every step.
        </p>
        <Hindi className="mt-4 text-base text-slate-500 leading-relaxed">
          डॉ. दीक्षित वाराणसी के प्रतिष्ठित और जाने-माने गैस्ट्रोएंटरोलॉजिस्ट
          हैं। उन्होंने IMS BHU से गैस्ट्रोएंटरोलॉजी में DM की है और लंका स्थित
          अपने क्लिनिक में पेट व लीवर के मरीज़ों का इलाज करते हैं।
        </Hindi>
        <Link
          to="/treatments"
          data-testid="about-explore-treatments-link"
          className="mt-6 inline-flex items-center gap-2 font-heading text-base font-bold text-sky-700 transition-colors hover:text-sky-500"
        >
          See all treatments
          <ArrowRight size={17} strokeWidth={2.5} />
        </Link>
      </Reveal>
      <Reveal delay={0.15} className="lg:col-span-2">
        <div
          data-testid="credentials-card"
          className="rounded-2xl border border-slate-200 bg-white p-7 shadow-[0_1px_2px_rgba(11,30,54,0.05)]"
        >
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-teal-600">
            Credentials
          </p>
          <ul className="mt-5 space-y-4">
            {[
              "DM — Gastroenterology, IMS BHU",
              "MD — Internal Medicine",
              "MBBS",
              "Senior Gastro & Liver specialist in Varanasi",
            ].map((item) => (
              <li key={item} className="flex gap-3 text-base text-slate-700">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-sky-500" />
                {item}
              </li>
            ))}
          </ul>
          <div className="mt-6 rounded-xl bg-sky-50 px-4 py-3 text-sm font-medium text-sky-800">
            Consultations in Hindi &amp; English
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const CareGrid = () => (
  <section className="bg-slate-50/70 border-y border-slate-100" data-testid="care-section">
    <div className="mx-auto max-w-6xl px-5 sm:px-8 py-20 sm:py-24">
      <Reveal>
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-sky-600">
          What we treat
        </p>
        <h2 className="mt-3 max-w-2xl font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy">
          Comprehensive Gastro &amp; Liver Care
        </h2>
        <p className="mt-4 max-w-2xl text-base text-slate-600 leading-relaxed">
          All stomach, intestine, liver, gallbladder and pancreas problems are
          treated at one place — from the first consultation to endoscopy,
          medicines and follow-up.
        </p>
        <Hindi className="mt-2 text-base text-slate-500">
          पेट, आंत, लीवर, पित्ताशय और अग्न्याशय — हर समस्या का इलाज एक ही जगह।
        </Hindi>
      </Reveal>
      <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {CARE_CARDS.map((card, i) => (
          <Reveal key={card.title} delay={0.06 * i}>
            <div
              data-testid={`bento-care-card-${i}`}
              className="group h-full rounded-2xl border border-slate-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-sky-300 hover:shadow-[0_12px_32px_-12px_rgba(2,132,199,0.25)]"
            >
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-sky-50 to-teal-50 text-sky-600 transition-colors group-hover:text-teal-600">
                <card.icon size={22} strokeWidth={2} />
              </div>
              <h3 className="mt-5 font-heading text-xl font-semibold text-navy">
                {card.title}
              </h3>
              <p className="mt-2.5 text-base text-slate-600 leading-relaxed">
                {card.text}
              </p>
              <Hindi className="mt-3 text-sm text-slate-500">{card.hindi}</Hindi>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Conditions = () => (
  <section className="mx-auto max-w-6xl px-5 sm:px-8 py-20 sm:py-24" data-testid="conditions-section">
    <Reveal>
      <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy">
        Conditions treated
      </h2>
      <p className="mt-3 max-w-2xl text-base text-slate-600 leading-relaxed">
        Dr. Dixit treats the full range of stomach, intestine, liver,
        gallbladder and pancreas conditions. Tap any condition to read how it
        is diagnosed and treated on the Treatments page.
      </p>
      <Hindi className="mt-2 text-base text-slate-500">
        किसी भी समस्या पर क्लिक करें और उसका इलाज विस्तार से पढ़ें।
      </Hindi>
    </Reveal>
    <Reveal delay={0.1}>
      <div className="mt-8 flex flex-wrap gap-3">
        {CONDITIONS.map((c, i) => (
          <Link
            key={c.label}
            to={`/treatments#${c.hash}`}
            data-testid={`condition-tag-${i}`}
            className="rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 transition-all duration-200 hover:-translate-y-0.5 hover:border-sky-400 hover:bg-sky-50 hover:text-sky-700"
          >
            {c.label}
          </Link>
        ))}
      </div>
    </Reveal>
  </section>
);

const WhyConsult = () => (
  <section className="bg-navy" data-testid="why-consult-section">
    <div className="mx-auto max-w-6xl px-5 sm:px-8 py-20 sm:py-24">
      <Reveal>
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-sky-300">
          Why consult Dr. Dixit
        </p>
        <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
          A name Varanasi trusts for stomach &amp; liver problems
        </h2>
        <p className="mt-4 max-w-2xl text-base text-slate-400 leading-relaxed">
          Patients choose Dr. Dixit for his specialist training, his
          experience, and the way he explains every problem and its treatment
          before starting it.
        </p>
        <Hindi className="mt-2 text-base text-slate-400">
          सही जाँच, साफ़ बात और भरोसेमंद इलाज।
        </Hindi>
      </Reveal>
      <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {WHY.map((w, i) => (
          <Reveal key={w.title} delay={0.06 * i}>
            <div
              data-testid={`why-consult-card-${i}`}
              className="h-full rounded-2xl border border-white/10 bg-navy-800/60 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-sky-400/40"
            >
              <w.icon size={24} className="text-sky-400" strokeWidth={2} />
              <h3 className="mt-4 font-heading text-lg font-semibold text-white">
                {w.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">
                {w.text}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const LocationCard = () => (
  <section className="mx-auto max-w-6xl px-5 sm:px-8 py-20 sm:py-24" data-testid="location-section">
    <Reveal>
      <div className="grid overflow-hidden rounded-3xl border border-slate-200 bg-white lg:grid-cols-2">
        <div className="p-8 sm:p-12">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-sky-600">
            Find us
          </p>
          <h2 className="mt-3 font-heading text-2xl sm:text-3xl font-bold tracking-tight text-navy">
            Clinic in Lanka, behind V2 Mall
          </h2>
          <p className="mt-5 flex gap-3 text-base leading-relaxed text-slate-600">
            <MapPin size={20} className="mt-1 shrink-0 text-teal-600" />
            {SITE.address}
          </p>
          <p className="mt-4 text-base text-slate-600 leading-relaxed">
            The clinic is easy to reach from Lanka crossing, Sunderpur and the
            BHU gate. V2 Mall is the main landmark — the clinic is directly
            behind it.
          </p>
          <Hindi className="mt-3 text-base text-slate-500">
            क्लिनिक लंका में V2 मॉल के ठीक पीछे स्थित है — पहुँचना आसान है।
          </Hindi>
          <div className="mt-8 flex flex-wrap gap-4">
            <a
              href={SITE.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="location-directions-button"
              className="inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 font-heading text-sm font-bold text-white transition-all duration-200 hover:bg-navy-800 hover:-translate-y-0.5"
            >
              <MapPin size={16} strokeWidth={2.5} />
              Get Directions
            </a>
            <Link
              to="/contact"
              data-testid="location-contact-link"
              className="inline-flex items-center gap-2 rounded-full border-2 border-navy/15 px-6 py-3 font-heading text-sm font-bold text-navy transition-all duration-200 hover:border-sky-400 hover:text-sky-700"
            >
              Contact details
              <ArrowRight size={16} strokeWidth={2.5} />
            </Link>
          </div>
        </div>
        <div className="hero-grid-lines relative flex min-h-[240px] items-center justify-center bg-sky-50/60 p-10">
          <svg width="220" height="220" viewBox="0 0 200 200" fill="none" aria-hidden="true">
            <circle cx="100" cy="100" r="80" stroke="#BAE6FD" strokeWidth="1" />
            <circle cx="100" cy="100" r="52" stroke="#BAE6FD" strokeWidth="1" />
            <path d="M100 20v160M20 100h160" stroke="#E0F2FE" strokeWidth="1" />
            <circle cx="100" cy="100" r="8" fill="#0284C7" />
            <circle cx="100" cy="100" r="16" stroke="#0284C7" strokeOpacity="0.4" strokeWidth="2" />
          </svg>
        </div>
      </div>
    </Reveal>
  </section>
);

const Faq = () => (
  <section className="bg-slate-50/70 border-y border-slate-100" data-testid="faq-section">
    <div className="mx-auto max-w-3xl px-5 sm:px-8 py-20 sm:py-24">
      <Reveal>
        <h2 className="text-center font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy">
          Questions patients ask
        </h2>
        <Hindi className="mt-3 text-center text-base text-slate-500">
          मरीज़ अक्सर पूछने वाले सवाल और उनके सीधे जवाब।
        </Hindi>
      </Reveal>
      <Reveal delay={0.1}>
        <Accordion type="single" collapsible className="mt-10 space-y-3">
          {FAQS.map((f, i) => (
            <AccordionItem
              key={f.q}
              value={`faq-${i}`}
              data-testid={`faq-accordion-item-${i}`}
              className="rounded-xl border border-slate-200 bg-white px-5 data-[state=open]:border-sky-300"
            >
              <AccordionTrigger className="py-4 text-left font-heading text-base font-semibold text-navy hover:no-underline [&[data-state=open]>svg]:rotate-180">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="pb-4 text-base leading-relaxed text-slate-600">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);

const FinalCta = () => (
  <section className="bg-navy-900" data-testid="final-cta-section">
    <div className="mx-auto max-w-6xl px-5 sm:px-8 py-20 text-center">
      <Reveal>
        <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-white">
          Stomach or liver problem?
          <span className="block text-sky-400">Call a specialist today.</span>
        </h2>
        <Hindi className="mt-4 text-base text-slate-400">
          पेट या लीवर की परेशानी है? आज ही विशेषज्ञ से बात करें।
        </Hindi>
        <a
          href={SITE.phoneHref}
          data-testid="final-cta-call-button"
          className="mt-8 inline-flex items-center gap-3 rounded-full bg-sky-500 px-9 py-4 font-heading text-lg font-bold text-white transition-all duration-200 hover:bg-sky-400 hover:-translate-y-0.5"
        >
          <Phone size={20} strokeWidth={2.5} />
          Call {SITE.phoneDisplay}
        </a>
        <p className="mt-5 text-sm text-slate-500">
          {SITE.hours} · Lanka, Varanasi
        </p>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Seo
        title="Dr. Dixit — Gastroenterologist & Liver Specialist in Varanasi | DM (Gastro), IMS BHU"
        description="Dr. Dixit, DM (Gastro) – IMS BHU, is a reputed gastroenterologist in Varanasi. Treatment for acidity, fatty liver, jaundice, IBS, endoscopy & colonoscopy in Lanka. Call +91 93696 98080. Mon–Sun 9 AM–5 PM."
        path="/"
        jsonLd={[physicianSchema, faqSchema]}
      />
      <Hero />
      <AppointmentStrip />
      <Marquee />
      <About />
      <CareGrid />
      <Conditions />
      <WhyConsult />
      <LocationCard />
      <Faq />
      <FinalCta />
    </>
  );
}
